/**
 * Call ID minting and parsing.
 *
 * Format: JEFF-XXXXXX where X is drawn from a Crockford-ish alphabet with the
 * visually ambiguous glyphs (I, L, O, U, 0, 1) removed, so an ID survives being
 * read aloud on a livestream or retyped from a screenshot. Example: JEFF-7F3A2B.
 */

export const ID_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const ID_PREFIX = 'JEFF-';
export const ID_BODY_LENGTH = 6;

const ID_PATTERN = new RegExp(`^${ID_PREFIX}[${ID_ALPHABET}]{${ID_BODY_LENGTH}}$`);

/**
 * Mints an ID from cryptographically strong bytes.
 *
 * Rejection sampling keeps the alphabet uniform: 256 % 30 !== 0, so naively
 * taking `byte % 30` would over-weight the first 16 characters. Bytes at or
 * above the largest multiple of 30 are discarded instead.
 */
export function generateCallId(getRandomValues: (b: Uint8Array) => Uint8Array = defaultRandom): string {
  const n = ID_ALPHABET.length;
  const ceiling = Math.floor(256 / n) * n; // 240
  let body = '';

  while (body.length < ID_BODY_LENGTH) {
    const bytes = getRandomValues(new Uint8Array(ID_BODY_LENGTH * 2));
    for (const byte of bytes) {
      if (byte >= ceiling) continue;
      body += ID_ALPHABET[byte % n];
      if (body.length === ID_BODY_LENGTH) break;
    }
  }

  return ID_PREFIX + body;
}

function defaultRandom(b: Uint8Array): Uint8Array {
  crypto.getRandomValues(b);
  return b;
}

export function isValidCallId(value: unknown): value is string {
  return typeof value === 'string' && ID_PATTERN.test(value);
}

/**
 * Accepts what a human is likely to paste — lower case, a missing prefix, or
 * surrounding whitespace — and returns the canonical form, or null.
 */
export function normaliseCallId(value: string): string | null {
  let v = value.trim().toUpperCase();
  if (!v.startsWith(ID_PREFIX)) v = ID_PREFIX + v.replace(/^JEFF-?/, '');
  return isValidCallId(v) ? v : null;
}
